import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

// Note: Install Monaco Editor: npm install @monaco-editor/react
// For now, using textarea as fallbacka
const CodeEditor = ({ value, onChange, language }) => {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-full font-mono text-sm p-4 bg-gray-900 text-gray-100 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder={`Write your ${language} code here...`}
      spellCheck={false}
    />
  );
};

const PlacementCodingTestPage = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const roundType = searchParams.get('type') || 'coding';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [problems, setProblems] = useState([]);
  const [currentProblem, setCurrentProblem] = useState(0);
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [roundId, setRoundId] = useState(null);
  const [isResuming, setIsResuming] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const session = SessionManager.getSession('candidate');
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  const languages = [
    { id: 'python', name: 'Python', icon: '🐍' },
    { id: 'java', name: 'Java', icon: '☕' },
    { id: 'cpp', name: 'C++', icon: '⚡' },
    { id: 'javascript', name: 'JavaScript', icon: '📜' }
  ];

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }
    // Reset state when round type changes (coding → advanced_coding)
    setProblems([]);
    setCurrentProblem(0);
    setCode('');
    setTestResults(null);
    setShowResults(false);
    loadProblems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundType]); // Re-run when round type changes

  const loadProblems = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/coding/questions?assessment_id=${assessmentId}&question_type=${roundType}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProblems(data.questions || []);
        setRoundId(data.round_id);
        setTimeRemaining(data.time_remaining_seconds || 600);
        setIsResuming(data.is_resuming || false);

        // Load previous submission if resuming
        if (data.is_resuming && data.current_question_index !== undefined) {
          setCurrentProblem(data.current_question_index);
          const currentQ = data.questions[data.current_question_index];
          if (currentQ?.submitted_solution) {
            setCode(currentQ.submitted_solution.code || '');
            setLanguage(currentQ.submitted_solution.language || 'python');
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        
        // Check if coding round is not configured
        if (response.status === 400 && errorData.detail?.includes('not configured')) {
          alert('Coding round is not available for this position. Redirecting to next round...');
          // Try to navigate to tech interview or results
          navigate(`/placement-training/assessment/${assessmentId}/tech-interview`);
        } else {
          alert('Failed to load problems. Please try again.');
          navigate(`/placement-training/assessment/${assessmentId}`);
        }
      }
    } catch (error) {
      console.error('Error loading problems:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Timer countdown
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeRemaining === 0 && !submitting && problems.length > 0) {
      console.log('Time up! Auto-moving to next problem...');
      handleNextProblem();
    }
  }, [timeRemaining, submitting, problems.length, currentProblem]);

  // Show warning when time is running low
  useEffect(() => {
    if (timeRemaining === 60 && problems.length > 0) {
      alert('⏰ 1 minute remaining! Will auto-move to next problem when time runs out.');
    } else if (timeRemaining === 30 && problems.length > 0) {
      alert('⏰ 30 seconds remaining! Will auto-move to next problem soon.');
    }
  }, [timeRemaining, problems.length]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) {
      alert('Please write some code before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      const currentQ = problems[currentProblem];

      const response = await fetch(
        `${apiUrl}/placement-training/coding/submit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            assessment_id: assessmentId,
            round_id: roundId,
            question_id: currentQ.id,
            question_number: currentQ.question_number,
            code,
            language
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
        setShowResults(true);

        // Update problem status
        const updatedProblems = [...problems];
        updatedProblems[currentProblem].is_submitted = true;
        updatedProblems[currentProblem].submitted_solution = {
          code,
          language,
          score: data.score,
          is_passed: data.is_passed
        };
        setProblems(updatedProblems);
      } else {
        alert('Failed to submit code. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting code:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextProblem = () => {
    if (currentProblem < problems.length - 1) {
      setCurrentProblem(prev => prev + 1);
      setCode('');
      setTestResults(null);
      setShowResults(false);
      setTimeRemaining(problems[0]?.duration_per_problem * 60 || 600);
    } else {
      handleCompleteCoding();
    }
  };

  const handleCompleteCoding = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/placement-training/coding/complete`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            assessment_id: assessmentId,
            round_id: roundId
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        const nextRound = data.next_round;

        // Navigate to the next round based on backend response
        if (nextRound === 'advanced_coding') {
          navigate(`/placement-training/assessment/${assessmentId}/coding?type=advanced_coding`);
        } else if (nextRound === 'tech_interview') {
          navigate(`/placement-training/assessment/${assessmentId}/tech-interview`);
        } else if (nextRound === 'hr_interview') {
          navigate(`/placement-training/assessment/${assessmentId}/hr-interview`);
        } else if (nextRound === 'completed') {
          navigate(`/placement-training/assessment/${assessmentId}/results`);
        } else {
          navigate(`/placement-training/assessment/${assessmentId}/tech-interview`);
        }
      }
    } catch (error) {
      console.error('Error completing coding round:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const currentQ = problems[currentProblem];
  const submittedCount = problems.filter(p => p.is_submitted).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 h-[calc(100vh-48px)]">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon name="code" size={24} className="text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{roundType === 'advanced_coding' ? 'Advanced Coding Challenge' : 'Coding Challenge'}</h1>
                <p className="text-sm text-gray-600">
                  Problem {currentProblem + 1} of {problems.length} • {submittedCount} submitted
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Timer */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                timeRemaining < 120 ? 'bg-red-50 text-red-700' : 'bg-purple-50 text-purple-700'
              }`}>
                <Icon name="schedule" size={20} />
                <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
                {timeRemaining <= 60 && timeRemaining > 0 && (
                  <span className="text-xs font-medium animate-pulse">Auto-next soon!</span>
                )}
              </div>

              {/* Language Selector */}
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {languages.map(lang => (
                  <option key={lang.id} value={lang.id}>
                    {lang.icon} {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Resume Banner */}
          {isResuming && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-purple-700">
                <Icon name="info" size={18} />
                <span className="text-sm font-medium">
                  Resuming coding round - Continue from problem {currentProblem + 1}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100%-100px)]">
          {/* Problem Description */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">{currentQ?.title}</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                currentQ?.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                currentQ?.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {currentQ?.difficulty}
              </span>
            </div>

            <div className="prose prose-sm max-w-none">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-line">{currentQ?.description}</p>
              </div>

              {currentQ?.input_format && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Input Format</h3>
                  <p className="text-gray-700 whitespace-pre-line">{currentQ?.input_format}</p>
                </div>
              )}

              {currentQ?.output_format && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Output Format</h3>
                  <p className="text-gray-700 whitespace-pre-line">{currentQ?.output_format}</p>
                </div>
              )}

              {currentQ?.constraints && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Constraints</h3>
                  <p className="text-gray-700 whitespace-pre-line">{currentQ?.constraints}</p>
                </div>
              )}

              {currentQ?.sample_input && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sample Input</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    {currentQ?.sample_input}
                  </pre>
                </div>
              )}

              {currentQ?.sample_output && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sample Output</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    {currentQ?.sample_output}
                  </pre>
                </div>
              )}

              {currentQ?.explanation && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Explanation</h3>
                  <p className="text-gray-700 whitespace-pre-line">{currentQ?.explanation}</p>
                </div>
              )}

              {currentQ?.tags && currentQ.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {currentQ.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Code Editor */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
            <div className="flex-1 mb-4">
              <CodeEditor
                value={code}
                onChange={setCode}
                language={language}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleSubmitCode}
                disabled={submitting || !code.trim()}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Running Tests...</span>
                  </>
                ) : (
                  <>
                    <Icon name="play_arrow" size={20} />
                    <span>Submit & Test</span>
                  </>
                )}
              </button>

              {currentQ?.is_submitted && (
                <button
                  onClick={handleNextProblem}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                >
                  <span>{currentProblem < problems.length - 1 ? 'Next Problem' : 'Complete Round'}</span>
                  <Icon name="arrow_forward" size={20} />
                </button>
              )}
            </div>

            {/* Previous Submission */}
            {currentQ?.is_submitted && currentQ?.submitted_solution && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">Previous Submission</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    currentQ.submitted_solution.is_passed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    Score: {currentQ.submitted_solution.score}%
                  </span>
                </div>
                <p className="text-xs text-blue-700">
                  You can resubmit to improve your score
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Test Results Modal */}
        {showResults && testResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Test Results</h3>
                <button
                  onClick={() => setShowResults(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="close" size={24} />
                </button>
              </div>

              {/* Overall Score */}
              <div className={`p-6 rounded-xl mb-6 ${
                testResults.is_passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium mb-1 ${testResults.is_passed ? 'text-green-700' : 'text-red-700'}">
                      {testResults.is_passed ? 'All Tests Passed!' : 'Some Tests Failed'}
                    </div>
                    <div className="text-3xl font-bold ${testResults.is_passed ? 'text-green-900' : 'text-red-900'}">
                      {testResults.test_cases_passed} / {testResults.test_cases_total}
                    </div>
                  </div>
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    testResults.is_passed ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <Icon 
                      name={testResults.is_passed ? 'check_circle' : 'cancel'} 
                      size={32} 
                      className={testResults.is_passed ? 'text-green-600' : 'text-red-600'}
                    />
                  </div>
                </div>
              </div>

              {/* Test Cases */}
              <div className="space-y-3">
                {testResults.test_results?.map((result, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      result.passed
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        Test Case {result.test_case_number}
                        {result.is_hidden && ' (Hidden)'}
                      </span>
                      <Icon
                        name={result.passed ? 'check_circle' : 'cancel'}
                        size={20}
                        className={result.passed ? 'text-green-600' : 'text-red-600'}
                      />
                    </div>
                    {!result.is_hidden && (
                      <div className="text-sm space-y-1">
                        {result.input && (
                          <div>
                            <span className="text-gray-600">Input:</span>
                            <code className="ml-2 text-gray-900">{result.input}</code>
                          </div>
                        )}
                        {result.expected_output && (
                          <div>
                            <span className="text-gray-600">Expected:</span>
                            <code className="ml-2 text-gray-900">{result.expected_output}</code>
                          </div>
                        )}
                        {result.actual_output && (
                          <div>
                            <span className="text-gray-600">Your Output:</span>
                            <code className="ml-2 text-gray-900">{result.actual_output}</code>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowResults(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                {currentProblem < problems.length - 1 ? (
                  <button
                    onClick={handleNextProblem}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Next Problem
                  </button>
                ) : (
                  <button
                    onClick={handleCompleteCoding}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Complete Round
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementCodingTestPage;
